import { useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import { api, User } from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Spinner, EmptyState, Select,
} from '../components/ui';
import { GanttTimeline } from '../components/GanttTimeline';

type Mode = 'staff' | 'team' | 'engagement';

const MODE_OPTIONS: Array<{ value: Mode; label: string }> = [
  { value: 'staff',      label: 'By staff member' },
  { value: 'team',       label: 'By team' },
  { value: 'engagement', label: 'By engagement' },
];

const WINDOW_OPTIONS: Array<{ value: string; label: string; months: number }> = [
  { value: '3',  label: '3 months',  months: 3 },
  { value: '6',  label: '6 months',  months: 6 },
  { value: '12', label: '12 months', months: 12 },
];

function todayYMD(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Window: half the months in the past, half in the future, centered on today. */
function computeWindow(months: number): { from: string; to: string } {
  const now = new Date();
  const half = Math.ceil(months / 2);
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - half, 1));
  const to   = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + (months - half) + 1, 0));
  return {
    from: from.toISOString().slice(0, 10),
    to:   to.toISOString().slice(0, 10),
  };
}

export default function GanttPage() {
  const { user } = useAuth();
  const [modeStr, setModeStr] = useUrlState('mode', 'staff');
  const mode: Mode = modeStr === 'team' || modeStr === 'engagement' ? modeStr : 'staff';
  const [subjectId, setSubjectId] = useUrlState('id', user?.jira_user_id ?? '');
  const [windowStr, setWindowStr] = useUrlState('window', '6');
  const windowMonths = parseInt(windowStr) || 6;

  const { from, to } = useMemo(() => computeWindow(windowMonths), [windowMonths]);

  const { data: members } = useQuery(() => api.team.members(), []);
  const { data: subjects } = useQuery(() => api.gantt.subjects(), []);

  // When the mode changes, the subject may become invalid for the new mode
  // (e.g. a user id is meaningless when viewing engagements). Reset it to a
  // sensible default once we know what's available.
  useEffect(() => {
    if (!subjects || !members) return;
    if (mode === 'staff') {
      const valid = (members.members ?? []).some((m: User) => m.jira_user_id === subjectId);
      if (!valid) setSubjectId(user?.jira_user_id ?? (members.members[0]?.jira_user_id ?? ''));
    } else if (mode === 'team') {
      const valid = (subjects.teams ?? []).some((t) => t.name === subjectId);
      if (!valid) setSubjectId(subjects.teams[0]?.name ?? '');
    } else {
      const valid = (subjects.engagements ?? []).some((e) => e.jira_issue_id === subjectId);
      if (!valid) setSubjectId(subjects.engagements[0]?.jira_issue_id ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, members, subjects]);

  const { data, isLoading, error } = useQuery(
    () => subjectId
      ? api.gantt.get({ mode, id: subjectId, from, to })
      : Promise.resolve(null),
    [mode, subjectId, from, to],
  );

  return (
    <>
      <SectionLabel>
        Timeline{data ? ` — ${data.subject.label}` : ''}
      </SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Gantt view</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            {from} → {todayYMD()} → {to}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Mode</span>
            <Select value={mode} onChange={setModeStr}>
              {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>

            {mode === 'staff' && (
              <>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>Staff</span>
                <Select value={subjectId} onChange={setSubjectId}>
                  {[...(members?.members ?? [])]
                    // Active first, then inactive — both sorted A-Z within
                    // their group. Inactive users get a suffix tag so they
                    // aren't mistaken for active teammates.
                    .sort((a: User, b: User) => {
                      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                      return a.display_name.localeCompare(b.display_name);
                    })
                    .map((m: User) => (
                      <option key={m.jira_user_id} value={m.jira_user_id}>
                        {m.display_name}{m.is_active ? '' : ' (inactive)'}
                      </option>
                    ))}
                </Select>
              </>
            )}
            {mode === 'team' && (
              <>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>Team</span>
                <Select value={subjectId} onChange={setSubjectId}>
                  {(subjects?.teams ?? []).length === 0 && <option value="">(no teams)</option>}
                  {[...(subjects?.teams ?? [])]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((t) => (
                      <option key={t.name} value={t.name}>{t.name} ({t.member_count})</option>
                    ))}
                </Select>
              </>
            )}
            {mode === 'engagement' && (
              <>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>Engagement</span>
                <Select value={subjectId} onChange={setSubjectId}>
                  {(subjects?.engagements ?? []).length === 0 && <option value="">(no engagements)</option>}
                  {[...(subjects?.engagements ?? [])]
                    .sort((a, b) => a.summary.localeCompare(b.summary))
                    .map((e) => (
                      <option key={e.jira_issue_id} value={e.jira_issue_id}>
                        {e.summary}{e.completed_date ? ` — shipped ${e.completed_date}` : ''}
                      </option>
                    ))}
                </Select>
              </>
            )}

            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>Window</span>
            <Select value={windowStr} onChange={setWindowStr}>
              {WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </span>
        </CardHeader>

        {!subjectId ? (
          <EmptyState message={
            mode === 'staff' ? 'Pick a staff member above.' :
            mode === 'team'  ? 'Pick a team above.' :
                               'Pick an engagement above.'
          } />
        ) : isLoading && !data ? <Spinner />
          : error ? <EmptyState message={error} />
          : !data ? null : (
          <div style={{ padding: 12 }}>
            <Legend />
            <GanttTimeline
              mode={data.mode}
              from={data.from}
              to={data.to}
              worklogs={data.worklogs}
              deliverables={data.deliverables}
              childIssues={data.children}
              engagements={data.engagements}
            />
          </div>
        )}
      </Card>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.45 }}>
        Bars span first worklog → shipped date (or today for open). Red vertical line = today.
        Green filled diamond = shipped; hollow diamond = planned due date. Red ticks = days with logged hours (saturation ∝ hours).
      </div>
    </>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 10, flexWrap: 'wrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 14, height: 5, border: '1px dashed var(--color-text-muted)', borderRadius: 2 }} />
        Planned (target start → due)
      </span>
      <LegendSwatch color="var(--color-green)"   label="Actual — shipped" />
      <LegendSwatch color="var(--color-amber)"   label="Actual — in progress" />
      <LegendDiamond color="var(--color-green)"  solid label="Shipped date" />
      <LegendDiamond color="var(--color-text-muted)" solid={false} label="Due date" />
      <LegendSwatch color="var(--color-brand-red)" label="Hours logged (saturation ∝ volume)" thin />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 1, height: 10, background: 'var(--color-brand-red)' }} />
        Today
      </span>
    </div>
  );
}
function LegendSwatch({ color, label, thin }: { color: string; label: string; thin?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: thin ? 2 : 12, height: 10, background: color, border: thin ? undefined : `1px solid ${color}`, borderRadius: 2 }} />
      {label}
    </span>
  );
}
function LegendDiamond({ color, solid, label }: { color: string; solid: boolean; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 9, height: 9, background: solid ? color : 'transparent', border: `1.5px solid ${color}`, transform: 'rotate(45deg)', display: 'inline-block' }} />
      {label}
    </span>
  );
}
